import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface User {
  id: string;
  email: string | null;
  name: string;
  isAnonymous: boolean;
  permissions: string[];
}

interface AdminNavigationDrawerProps {
  user: User | null;
  onLogout: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
  onPinChange?: (isPinned: boolean) => void;
}

export const AdminNavigationDrawer: React.FC<AdminNavigationDrawerProps> = ({
  user,
  onLogout,
  isMobileOpen,
  onMobileClose,
  onPinChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState<boolean>(() => {
    const saved = localStorage.getItem('adminNavDrawerPinned');
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(false);

  // Notify parent of initial pin state
  useEffect(() => {
    if (onPinChange) {
      onPinChange(isPinned);
    }
  }, []);

  // Detect mobile screen size (< 768px = md breakpoint)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    onLogout();
  };

  const handleSettings = () => {
    setIsUserMenuOpen(false);
    navigate('/chat');
    console.log('Navigate to settings');
    // TODO: Navigate to settings page when implemented
  };

  const handleManagePlan = () => {
    setIsUserMenuOpen(false);
    navigate('/manage-plan');
  };

  const togglePin = () => {
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    localStorage.setItem('adminNavDrawerPinned', String(newPinned));
    if (onPinChange) {
      onPinChange(newPinned);
    }
  };

  const handleNavItemClick = (route: string) => {
    navigate(route);
    // Close mobile drawer when nav item is clicked
    if (isMobile) {
      onMobileClose();
    }
  };

  // On mobile: only show if explicitly opened, can't be pinned
  // On desktop: show if pinned (no hover expand)
  const isExpanded = isMobile ? true : isPinned;
  const isVisible = isMobile ? isMobileOpen : true;

  const navItems = [
    {
      id: 'monitor-chats',
      route: '/admin/monitor-chats',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      label: 'Monitor Chats',
    },
    {
      id: 'manage-coupons',
      route: '/admin/coupons',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
      label: 'Manage Coupons',
    },
    {
      id: 'manage-llm-providers',
      route: '/admin/llm-providers',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
        </svg>
      ),
      label: 'Manage LLM Providers',
    },
  ];

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && isVisible && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={onMobileClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed left-0 top-0 h-screen bg-[#F5F5F5] border-r border-gray-300 shadow-lg transition-all duration-300 ease-in-out flex flex-col ${
          isMobile
            ? `z-50 w-64 transform ${isVisible ? 'translate-x-0' : '-translate-x-full'}`
            : `z-40 ${isExpanded ? 'w-64' : 'w-16'}`
        }`}
      >
        {/* Header with pin button (desktop only) or close button (mobile) */}
        <div className="flex items-center justify-between p-4 border-b border-gray-300 flex-shrink-0">
          {isMobile ? (
            <>
              <img
                src="/images/logos/icon-color.svg"
                alt="ChatHTL"
                className="h-8 w-8"
              />
              <button
                onClick={onMobileClose}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Close menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : isExpanded ? (
            <>
              <img
                src="/images/logos/icon-color.svg"
                alt="ChatHTL"
                className="h-8 w-8"
              />
              <button
                onClick={togglePin}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title={isPinned ? 'Unpin drawer' : 'Pin drawer'}
              >
                {isPinned ? (
                  <svg className="w-5 h-5 text-[#74AA9C]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={togglePin}
              className="p-1 hover:bg-gray-200 rounded transition-colors mx-auto"
              title="Expand drawer"
            >
              <img
                src="/images/logos/icon-color.svg"
                alt="ChatHTL"
                className="h-8 w-8"
              />
            </button>
          )}
        </div>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navItems.map((item) => {
          const isActive = location.pathname === item.route;
          return (
            <button
              key={item.id}
              onClick={() => handleNavItemClick(item.route)}
              className={`flex items-center w-full p-4 transition-colors ${
                isActive
                  ? 'bg-gray-200 text-[#080808]'
                  : 'text-[#080808] hover:bg-gray-200'
              } ${isExpanded ? 'justify-start' : 'justify-center'}`}
              title={!isMobile && !isExpanded ? item.label : undefined}
            >
              <div className="flex-shrink-0 text-[#74AA9C]">{item.icon}</div>
              {isExpanded && (
                <span className="ml-3 text-sm font-medium whitespace-nowrap">
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Back to Chat button */}
      <button
        onClick={() => navigate('/chat')}
        className={`w-full p-4 hover:bg-gray-200 transition-colors border-t border-gray-300 flex-shrink-0 ${isExpanded ? 'flex items-center justify-start' : 'flex justify-center'}`}
        title={!isExpanded ? 'Back to Chat' : undefined}
      >
        <svg className="w-6 h-6 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        {isExpanded && (
          <span className="ml-3 text-sm font-medium text-[#080808]">Back to Chat</span>
        )}
      </button>

      {/* Footer with user info */}
      {user && (
        <div ref={userMenuRef} className="relative border-t border-gray-300 flex-shrink-0">
          {/* User Menu Dropdown (only when expanded) */}
          {isExpanded && isUserMenuOpen && (
            <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2" style={{ width: 'calc(16rem - 1rem)' }}>
              {/* Email at top */}
              <div className="px-4 py-2 border-b border-gray-200">
                <p className="text-xs text-[#6B7280] truncate">
                  {user.email || 'Authenticated'}
                </p>
              </div>

              {/* Menu items */}
              <button
                onClick={handleSettings}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>

              <button
                onClick={handleManagePlan}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Manage Plan
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-[#080808] hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4 mr-3 text-[#74AA9C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Log out
              </button>
            </div>
          )}

          {/* Clickable user info */}
          <button
            onClick={() => isExpanded ? setIsUserMenuOpen(!isUserMenuOpen) : togglePin()}
            className={`w-full p-4 hover:bg-gray-200 transition-colors ${isExpanded ? 'text-left' : 'flex justify-center'}`}
          >
            {isExpanded ? (
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#74AA9C] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-white">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <div className="ml-3 overflow-hidden flex-1">
                  <p className="text-sm font-medium truncate text-[#080808]">{user.name || 'User'}</p>
                  <p className="text-xs text-[#4A4A4A] truncate">
                    {user.email || 'Authenticated'}
                  </p>
                </div>
                <svg
                  className={`w-4 h-4 text-[#4A4A4A] transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            ) : (
              <div className="w-8 h-8 bg-[#74AA9C] rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
              </div>
            )}
          </button>
        </div>
      )}
      </div>
    </>
  );
};
